/**
 * Summit Air offline queue for clock-in / clock-out actions.
 *
 * When the network is down (or the backend is unreachable), the engineer's
 * clock-in / clock-out is stored in localStorage with an ISO timestamp. When
 * connectivity returns, `flushQueue()` retries each queued action in order
 * against the live API. Successful actions leave the queue; permanent 4xx
 * failures (e.g. 400 no-active-session on a stale clock-out) are dropped with
 * an error toast so the user knows.
 *
 * Only mutation endpoints are queued (`/clock/start` + `/clock/stop`). GET
 * responses come from the service-worker read cache instead.
 */
import { api } from "@/lib/api";
import { toast } from "sonner";

const QUEUE_KEY = "summit_air_offline_queue";

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeQueue(q) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    // localStorage full / disabled — silently drop.
  }
}

export function queueSize() {
  return readQueue().length;
}

export function clearQueue() {
  writeQueue([]);
}

/** Add a pending action. Shape: {id, method, endpoint, payload, queuedAt}. */
export function enqueueAction(method, endpoint, payload) {
  const q = readQueue();
  q.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    method,
    endpoint,
    payload,
    queuedAt: new Date().toISOString(),
  });
  writeQueue(q);
  window.dispatchEvent(new CustomEvent("summit-air:queue-changed", { detail: { size: q.length } }));
}

/**
 * Send a mutation with automatic offline queue fallback. Returns the raw
 * axios response on success. On network failure, enqueues the payload and
 * throws a special error whose `.queued === true` so callers can show a
 * "Queued — will sync when back online" toast instead of a red error.
 */
export async function sendMutation(method, endpoint, payload = {}) {
  try {
    if (method === "POST") return await api.post(endpoint, payload);
    if (method === "DELETE") return await api.delete(endpoint);
    throw new Error(`Unsupported method: ${method}`);
  } catch (err) {
    // Only queue on network / offline errors. Real 4xx / 5xx from the server
    // should surface as normal so the user can react to them.
    const isOffline = !navigator.onLine
      || err?.code === "ERR_NETWORK"
      || err?.message === "Network Error"
      || err?.message?.includes("Failed to fetch");
    if (isOffline) {
      enqueueAction(method, endpoint, payload);
      const queuedErr = new Error("Queued — will sync when back online.");
      queuedErr.queued = true;
      throw queuedErr;
    }
    throw err;
  }
}

/**
 * Attempt to flush the pending queue in FIFO order. Returns {ok, fail, dropped}.
 * Stops on the first network failure so we don't burn through the whole queue
 * during a flap.
 */
export async function flushQueue() {
  let queue = readQueue();
  if (queue.length === 0) return { ok: 0, fail: 0, dropped: 0 };
  let ok = 0;
  let dropped = 0;
  while (queue.length > 0) {
    const item = queue[0];
    try {
      if (item.method === "POST") await api.post(item.endpoint, item.payload || {});
      else if (item.method === "DELETE") await api.delete(item.endpoint);
      queue.shift();
      writeQueue(queue);
      ok += 1;
    } catch (err) {
      // Definitive 4xx (e.g. "no active session" for a stale stop, or 400 for a
      // stale start when a session already exists) — drop and continue so one
      // stale entry doesn't block the rest.
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) {
        queue.shift();
        writeQueue(queue);
        dropped += 1;
      } else {
        // Network still down or 5xx — stop and retry later.
        return { ok, fail: queue.length, dropped };
      }
    }
  }
  window.dispatchEvent(new CustomEvent("summit-air:queue-changed", { detail: { size: 0 } }));
  return { ok, fail: 0, dropped };
}

/**
 * Mount-once background sync. Fires flush on:
 *  - app boot,
 *  - `online` event when network comes back,
 *  - every 30s poll (belt-and-braces).
 */
export function startOfflineSync() {
  const attempt = async (reason) => {
    if (!navigator.onLine) return;
    if (readQueue().length === 0) return;
    const result = await flushQueue();
    if (result.ok > 0) {
      toast.success(`Synced ${result.ok} queued action${result.ok === 1 ? "" : "s"} · ${reason}`);
    }
    if (result.dropped > 0) {
      toast.warning(`${result.dropped} queued action${result.dropped === 1 ? "" : "s"} could not sync (stale or invalid). Please re-check your session.`);
    }
  };

  window.addEventListener("online", () => attempt("back online"));
  const timer = setInterval(() => attempt("retry poll"), 30_000);
  // Fire once immediately in case we booted with pending items.
  setTimeout(() => attempt("boot"), 1500);
  return () => {
    clearInterval(timer);
  };
}
